import React, { useCallback, useEffect, useState } from "react";
import { Table, Modal, Button, Tag } from "antd";
import { BiEdit } from "react-icons/bi";
import { AiFillDelete } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { 
  getProducts, 
  deleteAProduct 
} from "../features/product/productSlice";
import { 
  getBrands, 
  deleteABrand 
} from "../features/brand/brandSlice";
import { 
  getCategories, 
  deleteAProductCategory 
} from "../features/pcategory/pcategorySlice";
import { delImg } from "../features/upload/uploadSlice";
import CustomModal from "../components/CustomModal";
import Addproduct from "./Addproduct";
import Addbrand from "./Addbrand";
import Addcat from "./Addcat";
import { useTheme } from "../contexts/ThemeContext";

const Catalog = () => {
  const [productOpen, setProductOpen] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteType, setDeleteType] = useState(""); // product, brand, or category
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const { darkMode } = useTheme();

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const productState = useSelector((state) => state?.product?.products);
  const productLoading = useSelector((state) => state?.product?.isLoading);
  const brandState = useSelector((state) => state.brand.brands);
  const brandLoading = useSelector((state) => state.brand.isLoading);
  const categoryState = useSelector((state) => state.pCategory.pCategories);
  const categoryLoading = useSelector((state) => state.pCategory.isLoading);
  const uploadLoading = useSelector((state) => state.upload.isLoading);

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getBrands());
    dispatch(getCategories());
  }, [dispatch]);

  // Product columns
  const productColumns = [
    {
      title: "SNo",
      dataIndex: "key",
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Title",
      dataIndex: "title",
      sorter: (a, b) => a.title.length - b.title.length,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Brand",
      dataIndex: "brand",
      sorter: (a, b) => a.brand.length - b.brand.length,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Category",
      dataIndex: "category",
      sorter: (a, b) => a.category.length - b.category.length,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Price",
      dataIndex: "price",
      sorter: (a, b) => a.price - b.price,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Original Price",
      dataIndex: "originalPrice",
      sorter: (a, b) => a.originalPrice - b.originalPrice,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Discounted Price",
      dataIndex: "discountedPrice",
      sorter: (a, b) => a.discountedPrice - b.discountedPrice,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Preorder",
      dataIndex: "preorder",
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Action",
      dataIndex: "action",
      className: "text-text-light dark:text-text-dark",
    },
  ];

  // Brand columns
  const brandColumns = [
    {
      title: "SNo",
      dataIndex: "key",
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Action",
      dataIndex: "action",
      className: "text-text-light dark:text-text-dark",
    },
  ];

  // Category columns
  const categoryColumns = [
    {
      title: "SNo",
      dataIndex: "key",
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => a.name.length - b.name.length,
      className: "text-text-light dark:text-text-dark",
    },
    {
      title: "Action",
      dataIndex: "action",
      className: "text-text-light dark:text-text-dark",
    },
  ];

  // Prepare product data
  const productData = [];
  for (let i = 0; i < productState?.length; i++) {
    productData.push({
      key: i + 1,
      title: productState[i].title,
      brand: productState[i].brand,
      category: productState[i].category,
      quantity: productState[i].quantity,
      price: `Rs. ${productState[i].price}`,
      originalPrice: productState[i].originalPrice ? `Rs. ${productState[i].originalPrice}` : "N/A",
      discountedPrice: productState[i].discountedPrice ? `Rs. ${productState[i].discountedPrice}` : "N/A",
      preorder: productState[i].isPreorder ? (
        <Tag color="blue">Preorder</Tag>
      ) : (
        <Tag color="green">In Stock</Tag>
      ),
      action: (
        <>
          <button
            className="fs-3 text-success bg-transparent border-0 dark:text-green-400 hover:text-green-600 dark:hover:text-green-300 transition-colors duration-200"
            onClick={() => {
              setEditMode(true);
              setEditId(productState[i]._id);
              setProductOpen(true);
            }}
          >
            <BiEdit />
          </button>
          <button
            className="ms-3 fs-3 text-danger bg-transparent border-0 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors duration-200"
            onClick={() => {
              setDeleteType("product");
              setDeleteId(productState[i]._id);
              setModalOpen(true);
            }}
          >
            <AiFillDelete />
          </button>
        </>
      ),
    });
  }

  // Prepare brand data
  const brandData = [];
  for (let i = 0; i < brandState?.length; i++) {
    brandData.push({
      key: i + 1,
      name: brandState[i].title,
      action: (
        <>
          <button
            className="fs-3 text-success bg-transparent border-0 dark:text-green-400 hover:text-green-600 dark:hover:text-green-300 transition-colors duration-200"
            onClick={() => {
              setEditMode(true);
              setEditId(brandState[i]._id);
              setBrandOpen(true);
            }}
          >
            <BiEdit />
          </button>
          <button
            className="ms-3 fs-3 text-danger bg-transparent border-0 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors duration-200"
            onClick={() => {
              setDeleteType("brand");
              setDeleteId(brandState[i]._id);
              setModalOpen(true);
            }}
          >
            <AiFillDelete />
          </button>
        </>
      ),
    });
  }

  // Prepare category data
  const categoryData = [];
  for (let i = 0; i < categoryState?.length; i++) {
    categoryData.push({
      key: i + 1,
      name: categoryState[i].title,
      action: (
        <>
          <button
            className="fs-3 text-success bg-transparent border-0 dark:text-green-400 hover:text-green-600 dark:hover:text-green-300 transition-colors duration-200"
            onClick={() => {
              setEditMode(true);
              setEditId(categoryState[i]._id);
              setCategoryOpen(true);
            }}
          >
            <BiEdit />
          </button>
          <button
            className="ms-3 fs-3 text-danger bg-transparent border-0 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors duration-200"
            onClick={() => {
              setDeleteType("category");
              setDeleteId(categoryState[i]._id);
              setModalOpen(true);
            }}
          >
            <AiFillDelete />
          </button>
        </>
      ),
    });
  }

  const handleDelete = () => {
    setIsDeleting(true);
    if (deleteType === "product") {
      dispatch(deleteAProduct(deleteId));
      dispatch(delImg(deleteId));
    } else if (deleteType === "brand") {
      dispatch(deleteABrand(deleteId));
    } else if (deleteType === "category") {
      dispatch(deleteAProductCategory(deleteId));
    }
    
    setModalOpen(false);
    // Refresh data after deletion
    setTimeout(() => {
      dispatch(getProducts());
      dispatch(getBrands());
      dispatch(getCategories());
      setIsDeleting(false);
    }, 100);
  };

  const getModalTitle = () => {
    switch (deleteType) {
      case "product":
        return "Are you sure you want to delete this Product?";
      case "brand":
        return "Are you sure you want to delete this Brand?";
      case "category":
        return "Are you sure you want to delete this Category?";
      default:
        return "Are you sure?";
    }
  };

  const handleProductModalClose = useCallback(() => {
    setProductOpen(false);
    setEditMode(false);
    setEditId(null);
    // Refresh all data
    setTimeout(() => {
      dispatch(getProducts());
      dispatch(getBrands());
      dispatch(getCategories());
    }, 300);
  }, [dispatch]);

  const handleBrandModalClose = useCallback(() => {
    setBrandOpen(false);
    setEditMode(false);
    setEditId(null);
    // Refresh all data
    setTimeout(() => {
      dispatch(getProducts());
      dispatch(getBrands());
      dispatch(getCategories());
    }, 300);
  }, [dispatch]);

  const handleCategoryModalClose = useCallback(() => {
    setCategoryOpen(false);
    setEditMode(false);
    setEditId(null);
    // Refresh all data
    setTimeout(() => {
      dispatch(getProducts());
      dispatch(getBrands());
      dispatch(getCategories());
    }, 300);
  }, [dispatch]);

  return (
    <div className="container-fluid bg-background-light dark:bg-background-dark">
      <h3 className="mb-4 title">Catalog Management</h3>
      
      {/* Products Section */}
      <div className="mb-5">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
          <h5 className="text-xl font-semibold text-text-light dark:text-text-dark">Products</h5>
          <Button 
            type="primary" 
            onClick={() => {
              setEditMode(false);
              setEditId(null);
              setProductOpen(true);
            }}
            className="w-full md:w-auto bg-primary-light dark:bg-primary-dark hover:bg-yellow-500 dark:hover:bg-yellow-600 text-text-light dark:text-text-dark border-none"
          >
            Add Product
          </Button>
        </div>
        <div className="overflow-x-auto bg-white dark:bg-surface-dark p-2 md:p-4 rounded-xl shadow dark:shadow-gray-800">
          {productLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light dark:border-primary-dark"></div>
              <span className="ml-2 text-text-light dark:text-text-dark">Loading products...</span>
            </div>
          ) : (
            <Table 
              columns={productColumns} 
              dataSource={productData} 
              pagination={{
                pageSize: 10,
                responsive: true,
                className: "text-text-light dark:text-text-dark",
              }}
              scroll={{ x: true }}
              size="small"
              className="min-w-full bg-white dark:bg-surface-dark"
            />
          )}
        </div>
      </div>

      {/* Brands Section */}
      <div className="mb-5">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
          <h5 className="text-xl font-semibold text-text-light dark:text-text-dark">Brands</h5>
          <Button 
            type="primary" 
            onClick={() => {
              setEditMode(false);
              setEditId(null);
              setBrandOpen(true);
            }}
            className="w-full md:w-auto bg-primary-light dark:bg-primary-dark hover:bg-yellow-500 dark:hover:bg-yellow-600 text-text-light dark:text-text-dark border-none"
          >
            Add Brand
          </Button>
        </div>
        <div className="overflow-x-auto bg-white dark:bg-surface-dark p-2 md:p-4 rounded-xl shadow dark:shadow-gray-800">
          {brandLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light dark:border-primary-dark"></div>
              <span className="ml-2 text-text-light dark:text-text-dark">Loading brands...</span>
            </div>
          ) : (
            <Table 
              columns={brandColumns} 
              dataSource={brandData} 
              pagination={{
                pageSize: 10,
                responsive: true,
                className: "text-text-light dark:text-text-dark",
              }}
              scroll={{ x: true }}
              size="small"
              className="min-w-full bg-white dark:bg-surface-dark"
            />
          )}
        </div>
      </div>

      {/* Categories Section */}
      <div className="mb-5">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
          <h5 className="text-xl font-semibold text-text-light dark:text-text-dark">Categories</h5>
          <Button 
            type="primary" 
            onClick={() => {
              setEditMode(false);
              setEditId(null);
              setCategoryOpen(true);
            }}
            className="w-full md:w-auto bg-primary-light dark:bg-primary-dark hover:bg-yellow-500 dark:hover:bg-yellow-600 text-text-light dark:text-text-dark border-none"
          >
            Add Category
          </Button>
        </div>
        <div className="overflow-x-auto bg-white dark:bg-surface-dark p-2 md:p-4 rounded-xl shadow dark:shadow-gray-800">
          {categoryLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-light dark:border-primary-dark"></div>
              <span className="ml-2 text-text-light dark:text-text-dark">Loading categories...</span>
            </div>
          ) : (
            <Table 
              columns={categoryColumns} 
              dataSource={categoryData} 
              pagination={{
                pageSize: 10,
                responsive: true,
                className: "text-text-light dark:text-text-dark",
              }}
              scroll={{ x: true }}
              size="small"
              className="min-w-full bg-white dark:bg-surface-dark"
            />
          )}
        </div>
      </div>

      {/* Product Modal */}
      <Modal
        title={editMode ? "Edit Product" : "Add Product"}
        centered
        open={productOpen}
        onOk={handleProductModalClose}
        onCancel={handleProductModalClose}
        width={1000}
        footer={null}
        className="responsive-modal"
        bodyStyle={{ 
          backgroundColor: darkMode ? '#334155' : '#ffffff',
          borderRadius: '0.75rem'
        }}
      >
        <div className="p-3 max-h-[70vh] overflow-y-auto">
          <Addproduct editId={editId} editMode={editMode} onSuccess={handleProductModalClose} />
        </div>
      </Modal>

      {/* Brand Modal */}
      <Modal
        title={editMode ? "Edit Brand" : "Add Brand"}
        centered
        open={brandOpen}
        onOk={handleBrandModalClose}
        onCancel={handleBrandModalClose}
        width={600}
        footer={null}
        className="responsive-modal"
        bodyStyle={{ 
          backgroundColor: darkMode ? '#334155' : '#ffffff',
          borderRadius: '0.75rem'
        }}
      >
        <div className="p-3 max-h-[70vh] overflow-y-auto">
          <Addbrand editId={editId} editMode={editMode} onSuccess={handleBrandModalClose} />
        </div>
      </Modal>

      {/* Category Modal */}
      <Modal
        title={editMode ? "Edit Category" : "Add Category"}
        centered
        open={categoryOpen}
        onOk={handleCategoryModalClose}
        onCancel={handleCategoryModalClose}
        width={600}
        footer={null}
        className="responsive-modal"
        bodyStyle={{ 
          backgroundColor: darkMode ? '#334155' : '#ffffff',
          borderRadius: '0.75rem'
        }}
      >
        <div className="p-3 max-h-[70vh] overflow-y-auto">
          <Addcat editId={editId} editMode={editMode} onSuccess={handleCategoryModalClose} />
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <CustomModal
        hideModal={() => setModalOpen(false)}
        open={modalOpen}
        performAction={handleDelete}
        title={getModalTitle()}
        loading={isDeleting}
      />
    </div>
  );
};

export default Catalog;